import React, { useState, useEffect, useCallback } from 'react';
import ReportWidgetRenderer from './ReportWidgetRenderer';
import AddWidgetModal from './AddWidgetModal';
import {
  Loader2, Plus, Save, FileText, ArrowLeft, Trash2, Pencil,
  BarChart3, Layout, CheckCircle, Clock, GripVertical,
  Maximize2, Minimize2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || 'https://app.legalpp.com.co/legalplus';

const CARD_BG = "bg-white dark:bg-slate-900/80 border border-gray-200 dark:border-slate-700/60 shadow-sm";

const SIZE_OPTIONS = [
  { id: 'sm', label: 'Pequeno', cols: 'col-span-1', h: 'h-[160px]' },
  { id: 'md', label: 'Mediano', cols: 'md:col-span-2 col-span-1', h: 'h-[280px]' },
  { id: 'lg', label: 'Grande', cols: 'md:col-span-3 col-span-1', h: 'h-[280px]' },
  { id: 'full', label: 'Completo', cols: 'md:col-span-4 col-span-1', h: 'h-[350px]' },
];

const DEFAULT_SIZE = {
  kpi: 'sm', area: 'lg', bar: 'md', pie: 'md',
  line: 'lg', heatmap: 'full', comparative: 'md',
};

const CHART_LABELS = {
  kpi: 'KPI', area: 'Area', bar: 'Barras', pie: 'Dona',
  line: 'Linea', heatmap: 'Calor', comparative: 'Comparativa',
};

// ==========================================
// REPORT LIST VIEW
// ==========================================
function ReportListView({ onCreateNew, onEdit, onBack }) {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReportes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/reportes`, { credentials: 'include', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (res.ok) setReportes(await res.json());
    } catch (e) {
      console.error('Error cargando reportes:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReportes(); }, [fetchReportes]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este reporte?')) return;
    try {
      await fetch(`${API_BASE}/reportes/${id}`, { method: 'DELETE', credentials: 'include', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      setReportes(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      console.error('Error eliminando:', e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            <ArrowLeft size={18} className="text-gray-600 dark:text-slate-300" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Mis Reportes</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">{reportes.length} reporte{reportes.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button onClick={onCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow-lg transition-all flex items-center gap-2 active:scale-95">
          <Plus size={18} /> Crear Reporte
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={32} /></div>
      )}

      {!loading && reportes.length === 0 && (
        <div className={`${CARD_BG} rounded-2xl p-16 text-center`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <Layout className="text-blue-500" size={32} />
          </div>
          <p className="text-gray-700 dark:text-slate-300 text-lg font-semibold">No hay reportes creados</p>
          <p className="text-gray-500 dark:text-slate-500 text-sm mt-1 mb-6">Crea dashboards personalizados con las metricas que necesitas</p>
          <button onClick={onCreateNew}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-6 rounded-xl shadow-lg transition-all inline-flex items-center gap-2 active:scale-95">
            <Plus size={18} /> Crear primer reporte
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {reportes.map(r => {
          const wCount = r.layout_json?.widgets?.length || 0;
          return (
            <div key={r.id} className={`${CARD_BG} rounded-2xl p-5 group hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer hover:-translate-y-0.5`}
              onClick={() => onEdit(r)}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white shadow">
                    <BarChart3 size={20} />
                  </div>
                  <div>
                    <h3 className="text-gray-900 dark:text-white font-bold">{r.nombre}</h3>
                    {r.descripcion && <p className="text-gray-500 dark:text-slate-400 text-xs mt-0.5 line-clamp-1">{r.descripcion}</p>}
                  </div>
                </div>
                <button onClick={(e) => handleDelete(r.id, e)}
                  className="text-gray-400 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-500">
                <span className="flex items-center gap-1"><Layout size={12} /> {wCount} widget{wCount !== 1 ? 's' : ''}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {r.updated_at?.split(' ')[0]}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// WIDGET CARD
// ==========================================
function WidgetCard({ widget, widgetIndex, size, onEdit, onRemove, onResize }) {
  const sizeConfig = SIZE_OPTIONS.find(s => s.id === size) || SIZE_OPTIONS[1];
  const nextSize = SIZE_OPTIONS[(SIZE_OPTIONS.findIndex(s => s.id === size) + 1) % SIZE_OPTIONS.length];

  return (
    <div className={`${sizeConfig.cols} ${CARD_BG} rounded-2xl overflow-hidden flex flex-col transition-all hover:shadow-md`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-slate-800/60 bg-gray-50/80 dark:bg-slate-900/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-800 dark:text-slate-200 truncate">{widget.titulo}</span>
          <span className="text-[9px] font-medium text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded shrink-0 uppercase">
            {CHART_LABELS[widget.tipoGrafico] || widget.tipoGrafico}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          <button onClick={() => onResize(widget.id, nextSize.id)} title={`Cambiar a ${nextSize.label}`}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800">
            {size === 'sm' || size === 'md' ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
          </button>
          <button onClick={() => onEdit(widget)} className="text-gray-400 hover:text-blue-500 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
            <Pencil size={12} />
          </button>
          <button onClick={() => onRemove(widget.id)} className="text-gray-400 hover:text-rose-500 transition-colors p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-900/20">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {/* Chart content */}
      <div className={`p-4 ${sizeConfig.h} bg-white dark:bg-slate-950/40`}>
        <div className="w-full h-full">
          <ReportWidgetRenderer widget={widget} widgetIndex={widgetIndex} />
        </div>
      </div>
    </div>
  );
}

// ==========================================
// REPORT EDITOR VIEW
// ==========================================
function ReportEditorView({ reporte, onBack, onSaved }) {
  const [nombre, setNombre] = useState(reporte?.nombre || '');
  const [descripcion, setDescripcion] = useState(reporte?.descripcion || '');
  const [widgets, setWidgets] = useState(reporte?.layout_json?.widgets || []);
  const [sizes, setSizes] = useState(reporte?.layout_json?.sizes || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);

  const handleAddWidget = (widget) => {
    if (editingWidget) {
      setWidgets(prev => prev.map(w => w.id === widget.id ? widget : w));
      setEditingWidget(null);
    } else {
      setWidgets(prev => [...prev, widget]);
      setSizes(prev => ({ ...prev, [widget.id]: DEFAULT_SIZE[widget.tipoGrafico] || 'md' }));
    }
    setSaved(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    setSizes(prev => { const n = { ...prev }; delete n[widgetId]; return n; });
    setSaved(false);
  };

  const handleResize = (widgetId, newSize) => {
    setSizes(prev => ({ ...prev, [widgetId]: newSize }));
    setSaved(false);
  };

  const handleEditWidget = (widget) => {
    setEditingWidget(widget);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!nombre.trim()) return;
    setSaving(true);
    try {
      const body = { nombre: nombre.trim(), descripcion: descripcion.trim(), layout_json: { widgets, sizes } };
      const isNew = !reporte?.id;
      const url = isNew ? `${API_BASE}/reportes` : `${API_BASE}/reportes/${reporte.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (onSaved) onSaved(data);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e) {
      console.error('Error guardando:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = useCallback(async () => {
    setExporting(true);
    try {
      const el = document.getElementById('report-canvas');
      if (!el) return;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#0f172a', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? 'landscape' : 'portrait' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`${nombre || 'Reporte'}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (e) {
      console.error('Error exportando:', e);
    } finally {
      setExporting(false);
    }
  }, [nombre]);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className={`${CARD_BG} rounded-2xl p-4`}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 w-full min-w-0">
            <button onClick={onBack} className="p-2 rounded-xl bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors shrink-0">
              <ArrowLeft size={18} className="text-gray-600 dark:text-slate-300" />
            </button>
            <div className="flex-1 min-w-0">
              <input type="text" value={nombre} onChange={e => { setNombre(e.target.value); setSaved(false); }} placeholder="Nombre del reporte..."
                className="bg-transparent text-xl font-bold text-gray-900 dark:text-white focus:outline-none w-full placeholder-gray-400 dark:placeholder-slate-600" />
              <input type="text" value={descripcion} onChange={e => { setDescripcion(e.target.value); setSaved(false); }} placeholder="Descripcion breve (opcional)"
                className="bg-transparent text-sm text-gray-500 dark:text-slate-400 focus:outline-none w-full mt-0.5 placeholder-gray-300 dark:placeholder-slate-700" />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => { setEditingWidget(null); setModalOpen(true); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded-xl transition-all flex items-center gap-2 text-sm active:scale-95">
              <Plus size={16} /> Widget
            </button>
            <button onClick={handleExportPDF} disabled={exporting || widgets.length === 0}
              className="bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-40 text-gray-700 dark:text-slate-200 font-medium py-2 px-4 rounded-xl transition-all flex items-center gap-2 text-sm">
              {exporting ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} PDF
            </button>
            <button onClick={handleSave} disabled={saving || !nombre.trim()}
              className={`font-medium py-2 px-4 rounded-xl shadow transition-all flex items-center gap-2 text-sm active:scale-95 ${saved ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white'}`}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : saved ? <CheckCircle size={16} /> : <Save size={16} />}
              {saving ? 'Guardando...' : saved ? 'Guardado' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {widgets.length === 0 && (
        <div className={`${CARD_BG} rounded-2xl p-16 text-center`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
            <BarChart3 className="text-emerald-500" size={32} />
          </div>
          <p className="text-gray-700 dark:text-slate-300 text-lg font-semibold">Agrega widgets para armar tu reporte</p>
          <p className="text-gray-500 dark:text-slate-500 text-sm mt-1 mb-6">Selecciona fuentes de datos y tipos de grafico</p>
          <button onClick={() => { setEditingWidget(null); setModalOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-6 rounded-xl shadow-lg transition-all inline-flex items-center gap-2 active:scale-95">
            <Plus size={18} /> Agregar Widget
          </button>
        </div>
      )}

      {/* Widget grid */}
      {widgets.length > 0 && (
        <div id="report-canvas" className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {widgets.map((widget, idx) => (
            <WidgetCard
              key={widget.id}
              widget={widget}
              widgetIndex={idx}
              size={sizes[widget.id] || DEFAULT_SIZE[widget.tipoGrafico] || 'md'}
              onEdit={handleEditWidget}
              onRemove={handleRemoveWidget}
              onResize={handleResize}
            />
          ))}
        </div>
      )}

      <AddWidgetModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingWidget(null); }} onAdd={handleAddWidget} editWidget={editingWidget} />
    </div>
  );
}

// ==========================================
// MAIN REPORT BUILDER
// ==========================================
export default function ReportBuilder({ onBack }) {
  const [view, setView] = useState('list');
  const [currentReporte, setCurrentReporte] = useState(null);

  const handleCreateNew = () => { setCurrentReporte(null); setView('editor'); };
  const handleEdit = (reporte) => { setCurrentReporte(reporte); setView('editor'); };
  const handleSaved = (s) => { setCurrentReporte(s); };
  const handleBackFromEditor = () => { setCurrentReporte(null); setView('list'); };

  if (view === 'editor') {
    return <ReportEditorView reporte={currentReporte} onBack={handleBackFromEditor} onSaved={handleSaved} />;
  }
  return <ReportListView onCreateNew={handleCreateNew} onEdit={handleEdit} onBack={onBack} />;
}
